import { FC } from 'react';
import { css } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { colors, spacing, fontStyles } from '../../../styles';
import { cleanMarkdownContent } from '../../../utils/contentCleaning';
import { ToolRendererProps } from './types';

export const AnalysisRenderer: FC<ToolRendererProps> = ({ output, isLoading, toolName }) => {
  if (isLoading) {
    const action = toolName?.includes('edge')
      ? 'detecting edge'
      : toolName?.includes('risk')
        ? 'analyzing risks'
        : toolName?.includes('scenario')
          ? 'simulating scenarios'
          : toolName?.includes('expectations') || toolName === 'growth_expectations'
            ? 'analyzing growth expectations'
            : toolName?.includes('pricing')
              ? 'analyzing pricing power'
              : 'analyzing';
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Currently {action}...
      </div>
    );
  }

  // Extract content from various possible locations
  const content =
    typeof output === 'string'
      ? output
      : output.content ||
        output.analysis ||
        output.summary ||
        output.output ||
        (typeof output === 'object' && Object.keys(output).length === 1 ? Object.values(output)[0] : '') ||
        '';

  // If we have substantial content, render it fully (more flexible than before)
  if (
    content.length > 50 &&
    (content.includes('## ') ||
      content.includes('### ') ||
      content.includes('|') ||
      content.includes('\n') ||
      content.includes('analysis') ||
      content.includes('score'))
  ) {
    return (
      <div
        css={css`
          color: ${colors.liquorice};
          font-size: 14px;
          line-height: 1.4;

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }

          /* Markdown styles matching ChatPage */
          & p {
            margin: 0 0 ${spacing.centi} 0;
            &:last-child {
              margin-bottom: 0;
            }
          }
          & h1 {
            ${fontStyles.lion}
            margin: 0 0 ${spacing.centi} 0;
          }
          & h2 {
            ${fontStyles.dog}
            margin: 0 0 ${spacing.centi} 0;
          }
          & h3 {
            font-size: 16px;
            font-weight: 600;
            margin: 0 0 ${spacing.milli} 0;
          }
          & ul,
          & ol {
            margin: ${spacing.centi} 0;
            padding-left: ${spacing.regular};
          }
          & li {
            margin: ${spacing.milli} 0;
          }
          & code {
            background-color: rgba(0, 0, 0, 0.1);
            padding: ${spacing.micro} ${spacing.milli};
            border-radius: 4px;
          }
          & pre {
            background-color: rgba(0, 0, 0, 0.1);
            padding: ${spacing.milli};
            border-radius: 4px;
            overflow-x: auto;
            margin: ${spacing.milli} 0;
          }
          & strong {
            font-weight: normal;
          }
          & em {
            font-style: normal;
          }
          & a {
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
          }

          /* Table styles matching ChatPage */
          & .table-wrapper {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            margin: ${spacing.deci} 0;
            border-radius: 8px;
            border: 1px solid ${colors.cloud};
          }

          & table {
            border-collapse: collapse;
            width: 100%;
            display: table;
            font-size: 0.9em;
            margin: 0;
          }

          & th,
          & td {
            border: none;
            border-bottom: 1px solid ${colors.cloud};
            padding: ${spacing.deci};
            text-align: left;
            vertical-align: top;
            white-space: normal;
            word-break: normal;
            line-height: 1.5;
          }

          & th {
            background-color: rgba(0, 0, 0, 0.05);
            font-weight: 600;
            min-width: 120px;
            white-space: nowrap;
            border-bottom: 2px solid ${colors.cloud};
          }

          & td {
            white-space: pre-line;

            & br {
              display: block;
              content: '';
              margin-top: 0.5em;
            }

            & ul,
            & ol {
              margin: 0.5em 0;
              padding-left: 1.5em;
            }

            & li {
              margin: 0.25em 0;
            }

            & strong {
              font-weight: normal;
            }

            & em {
              font-style: normal;
            }
          }

          & tr:nth-of-type(even) {
            background-color: rgba(0, 0, 0, 0.02);
          }

          & tr:last-child td {
            border-bottom: none;
          }

          html[data-theme='dark'] & {
            & .table-wrapper {
              border-color: ${colors.tungsten};
            }

            & th,
            & td {
              border-color: ${colors.tungsten};
            }

            & th {
              background-color: rgba(255, 255, 255, 0.05);
              border-bottom-color: ${colors.tungsten};
            }

            & tr:nth-of-type(even) {
              background-color: rgba(255, 255, 255, 0.02);
            }
          }
        `}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ({ children, ...props }) => (
              <div className="table-wrapper">
                <table {...props}>{children}</table>
              </div>
            ),
            td: ({ children, ...props }) => <td {...props}>{children}</td>,
            th: ({ children, ...props }) => <th {...props}>{children}</th>,
            strong: ({ children }) => <span>{children}</span>,
            em: ({ children }) => <span>{children}</span>,
          }}
        >
          {cleanMarkdownContent(content)}
        </ReactMarkdown>
      </div>
    );
  }

  // Fallback for short content or simple messages
  if (content && content.trim().length > 0) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.liquorice};
          line-height: 1.4;

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        {content.trim()}
      </div>
    );
  }

  return (
    <div
      css={css`
        font-size: 14px;
        color: ${colors.silver};
      `}
    >
      Analysis completed
    </div>
  );
};
